import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { useFinancialStore } from '@/store/useFinancialStore';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import { motion, AnimatePresence } from 'framer-motion';

interface AgentMessage {
  id: string;
  sender: string;
  receiver: string;
  content: string;
  timestamp: string;
}

interface AgentStatus {
  id: string;
  name: string;
  status: 'ACTIVE' | 'IDLE' | 'HEDGING' | 'FAULT';
  efficiency: number;
  lastAction: string;
}

export default function TradingAgentsPage() {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [command, setCommand] = useState("");

  const { loading: storeLoading } = useFinancialStore();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [msgResp, agentResp, stratResp] = await Promise.all([
        apiClient.getA2AMessages(),
        apiClient.getAgentStatus(),
        apiClient.getA2AStrategies()
      ]);

      if (msgResp.data) setMessages(msgResp.data as AgentMessage[]);
      if (agentResp.data) setAgents(agentResp.data as AgentStatus[]);
      if (stratResp.data) setStrategies(stratResp.data as any[]);
    } catch (err) {
      console.error('Failed to sync autonomous topography');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleInject = async () => {
    if (!command) return;
    setIsLoading(true);
    await apiClient.sendA2AMessage({
      sender: 'HUMAN_OPERATOR',
      receiver: 'NETWORK_CORE',
      content: command,
      timestamp: new Date().toISOString()
    });
    setCommand("");
    await fetchData();
    setIsLoading(false);
  };

  const handleDeploy = async () => {
    setIsLoading(true);
    await apiClient.createA2AStrategy({
      name: 'Dynamic Alpha Node',
      type: 'AUTONOMOUS_ARBITRAGE',
      risk_level: 'MEDIUM'
    });
    await fetchData();
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30 pb-12">
      {/* Header */}
      <header className="bg-bg-secondary/50 backdrop-blur-xl border-b border-border-primary sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl tracking-tight font-semibold">
              A2A Autonomous Network
            </h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${agents.length > 0 ? 'bg-brand-teal-light animate-pulse shadow-glow' : 'bg-white/10'}`} />
                <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Network Status: {agents.length > 0 ? 'Synchronized' : 'Offline'}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Agent Status Grid */}
          <div className="lg:col-span-1 space-y-6">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary px-1">Node Topography</h2>
            <div className="grid grid-cols-1 gap-4">
              {agents.length > 0 ? agents.map((agent) => (
                <GlassCard key={agent.id} variant="elevated" className="p-5 flex flex-col gap-4 group hover:border-brand-teal/30 transition-all">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-text-primary group-hover:text-brand-teal-light transition-colors">{agent.name}</h3>
                      <p className="text-[10px] text-text-tertiary font-mono uppercase mt-1">{agent.status}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-mono font-bold text-brand-teal-light">{agent.efficiency}%</div>
                      <div className="text-[9px] font-black text-text-tertiary uppercase">Efficiency</div>
                    </div>
                  </div>
                  <div className="w-full bg-bg-primary rounded-full h-1 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${
                        agent.status === 'ACTIVE' ? 'bg-brand-teal' : 
                        agent.status === 'HEDGING' ? 'bg-brand-plum' : 'bg-text-tertiary'
                      }`}
                      style={{ width: `${agent.efficiency}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-text-secondary font-medium italic">
                    Last: {agent.lastAction}
                  </p>
                </GlassCard>
              )) : (
                <div className="p-12 text-center border border-dashed border-white/5 rounded-3xl opacity-30">
                  <p className="text-[10px] font-black uppercase tracking-widest">No Nodes Detected</p>
                </div>
              )}
            </div>
            
            <GlassButton 
              variant="primary" 
              glowEffect 
              onClick={handleDeploy}
              disabled={isLoading}
              className="w-full py-4 text-xs font-black uppercase tracking-widest"
            >
              {isLoading ? 'Processing Deployment...' : 'Deploy New Strategy Node'}
            </GlassButton>
          </div>

          {/* Comms Link (Agent-to-Agent Logs) */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary px-1">Inter-Agent Comm Link</h2>
            <GlassCard variant="glow" className="p-0 overflow-hidden h-[600px] flex flex-col border-white/5">
              <div className="bg-bg-secondary/50 p-4 border-b border-white/5 flex justify-between items-center">
                 <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Autonomic Log Stream</span>
                 <span className="text-[9px] font-mono text-brand-teal-light">Encrypted (v7.1)</span>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                <AnimatePresence initial={false}>
                  {messages.map((msg) => (
                    <motion.div 
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-2"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                           <span className="text-[10px] font-black text-brand-teal-light tracking-wide">{msg.sender}</span>
                           <span className="text-[10px] text-text-tertiary">➔</span>
                           <span className="text-[10px] font-black text-brand-plum-light tracking-wide">{msg.receiver}</span>
                        </div>
                        <span className="text-[9px] font-mono text-text-tertiary">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <div className="p-4 bg-bg-primary/50 border border-white/5 rounded-2xl text-[13px] text-text-secondary leading-relaxed font-medium">
                        {msg.content}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {isLoading && messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-text-tertiary opacity-40">
                     <div className="w-10 h-10 rounded-full border-2 border-dashed border-brand-teal animate-spin" />
                     <p className="text-[10px] font-black uppercase tracking-widest">Intercepting Feed...</p>
                  </div>
                )}
              </div>
              
              <div className="p-4 bg-bg-secondary/80 backdrop-blur-xl border-t border-white/5">
                 <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={command}
                      onChange={(e) => setCommand(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleInject()}
                      placeholder="Inject manual protocol command..."
                      className="flex-1 bg-bg-primary/50 border border-white/5 rounded-xl px-4 py-2.5 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-brand-teal/30 text-text-primary"
                    />
                    <GlassButton 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleInject}
                      disabled={isLoading}
                      className="text-brand-teal-light px-6"
                    >
                      {isLoading ? 'Injecting...' : 'Inject'}
                    </GlassButton>
                 </div>
              </div>
            </GlassCard>
          </div>
        </div>

        {/* Strategies Section */}
        <div className="pt-8">
           <h2 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary px-1 mb-6">Active Strategy Matrix</h2>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {strategies.length > 0 ? strategies.map((strategy, i) => (
                <GlassCard key={i} variant="subtle" className="p-6 group cursor-pointer hover:bg-white/[0.02] transition-all">
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="font-bold text-text-primary text-lg tracking-tight group-hover:text-brand-teal-light transition-colors">{strategy.name}</h3>
                    <div className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                      strategy.status === 'Operational' ? 'bg-brand-teal/10 text-brand-teal-light' : 'bg-text-tertiary/10 text-text-tertiary'
                    }`}>
                      {strategy.status}
                    </div>
                  </div>
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-2xl font-bold text-text-primary font-mono">{strategy.yield}</div>
                      <div className="text-[9px] font-black text-text-tertiary uppercase tracking-widest">Projected Alpha</div>
                    </div>
                    <div className="text-right">
                       <div className={`text-xs font-bold uppercase tracking-widest ${
                         strategy.risk === 'Low' ? 'text-brand-teal-light' : strategy.risk === 'Medium' ? 'text-amber-400' : 'text-brand-plum-light'
                       }`}>
                         {strategy.risk} Risk
                       </div>
                    </div>
                  </div>
                </GlassCard>
              )) : !isLoading && (
                <div className="col-span-full py-12 text-center rounded-3xl border border-dashed border-white/5 bg-white/[0.01]">
                   <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary opacity-30">No Active Real-World Strategies Intercepted</p>
                </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
}
